import React, { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import { IoCheckbox, IoSquareOutline, IoCheckboxOutline } from 'react-icons/io5';
import { selectCartItems, selectCartStatus, selectCartTotalFinalPrice, selectCartLMD, cartTotalLMD } from '../../../../redux/slices/cartSlice';
import { useSelector, useDispatch } from 'react-redux';
import { useSearchParams, useOutletContext } from 'react-router-dom';
import CartProductCard from './CartProductCard';
import { CheckOutBox } from './CheckOutBox';
import { CartProductQuantity } from './CartProductQuantity';
import { CartApiService } from '../../../service/cartApiService';
import { fetchCartItems } from '../../../../redux/slices/cartSlice';
import { getAuthLocalStorage } from '../../../../storage/localStorage';
import { SellingModes } from '../../../../helper/constants';
import { ProductsApiService } from '../../../../service/website/productsApiService';
import Loader from '../../../../component/common/loader';

// Helper to check if dropshipping is available for a product
const isDropshippingAvailableFromTags = (tagInfoList = []) => {
  const isOnePsaleTag = tagInfoList.find(tag => tag.key === 'isOnePsale');
  // Dropshipping is available if isOnePsale is not false
  return !(isOnePsaleTag?.value === false || isOnePsaleTag?.value === 'false');
};

export default function Cart() {
  const { setPriceSummary, priceSummary, totalLMDWithGST, totalLMDCharges, gstOnLMD, shippingMethod } = useOutletContext() || {};
  const data = useSelector(selectCartItems);
  const cartStatus = useSelector(selectCartStatus);
  const cartTotalFinalPrice = useSelector(selectCartTotalFinalPrice);
  const cartLMD = useSelector(selectCartLMD);
  const cartTotalLMDValue = useSelector(cartTotalLMD);
  const dispatch = useDispatch();
  const [cart, setCart] = useState([]);
  const [selectedItems, setSelectedItems] = useState(new Set());
  const [updatingVariant, setUpdatingVariant] = useState(null);
  const [pendingSaveVariants, setPendingSaveVariants] = useState(new Set());
  const [isAllLoadingCard, setAllLoadingCard] = useState({ all: false });
  const [searchParams] = useSearchParams();
  const [targetedUpdateCartItemId, setTargetedUpdateCartItemId] = useState(null);
  const abortControllerRef = useRef(null);
  const lastProcessedQuantitiesRef = useRef(new Map());
  const cartAllProductsMOQRequiredRef = useRef(new Set());

  // Fetch cart items on component mount
  useEffect(() => {
    dispatch(fetchCartItems());
  }, [dispatch]);

  useEffect(() => {
    // Filter out items with null products
    const validData = data.filter(item => item?.product?._id);

    if (searchParams.get('product')) {
      const arr = validData.filter(cartItem => {
        return cartItem.product._id === searchParams.get('product');
      });
      setCart(arr);
    } else {
      setCart(validData);
    }
    // Auto-select all variants when cart loads
    const initialSelectedItems = new Set();
    validData.forEach(item => {
      if (item.product?.variants && item.product.variants.length > 0) {
        item.product.variants.forEach(v => {
          initialSelectedItems.add(`${item._id}-${v.skuId}`);
        });
      }
    });
    setSelectedItems(initialSelectedItems);

    // Check for completed updates and clear pending save states
    if (pendingSaveVariants.size > 0) {
      const updatedSet = new Set(pendingSaveVariants);
      let hasChanges = false;

      pendingSaveVariants.forEach(cartItemId => {
        const cartItem = validData.find(item => item._id === cartItemId);
        if (cartItem && cartItem.product?.variants && cartItem.product.variants.length > 0) {
          // Check first variant's quantity (they should all have same total qty logic)
          const currentQty = cartItem.product.variants.reduce((sum, v) => sum + (v.quantity || 0), 0);
          const lastProcessedQty = lastProcessedQuantitiesRef.current.get(cartItemId);

          // If the quantity in Redux matches what we're trying to save, clear pending state
          if (lastProcessedQty !== undefined && currentQty === lastProcessedQty) {
            // console.log(`✅ Pending save cleared for ${cartItemId}: qty matched (${currentQty})`);
            updatedSet.delete(cartItemId);
            hasChanges = true;
          }
        }
      });

      if (hasChanges) {
        setPendingSaveVariants(updatedSet);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, searchParams]);

  const handleSelectItem = (itemId, skuId = null) => {
    // If skuId is provided, select individual variant; otherwise select entire cart item
    const selectionKey = skuId ? `${itemId}-${skuId}` : itemId;
    setSelectedItems(prev => {
      const newSet = new Set(prev);
      if (newSet.has(selectionKey)) {
        newSet.delete(selectionKey);
      } else {
        newSet.add(selectionKey);
      }
      return newSet;
    });
  };

  // No grouping needed - API response already has proper structure

  // Handle quantity change for a variant
  const handleQuantityChange = async (cartItemId, newQuantity, variant, pricingMode, breakdown = null) => {
    // console.log('Parent handleQuantityChange - Received newQuantity:', newQuantity, 'Variant skuId:', variant?.skuId, 'Variant current quantity:', variant?.quantity);
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    abortControllerRef.current = new AbortController();
    const signal = abortControllerRef.current.signal;

    // Set updating and pending save states
    setUpdatingVariant(cartItemId);
    setPendingSaveVariants(prev => new Set(prev).add(cartItemId));

    // Set the targeted update cart item ID to trigger targeted shipping cost calculation
    setTargetedUpdateCartItemId(cartItemId);

    // Store the quantity we're trying to save
    lastProcessedQuantitiesRef.current.set(cartItemId, newQuantity);
    try {
      const authData = getAuthLocalStorage();
      if (!authData.isLoggedIn || !authData.loginHeader) {
        setPendingSaveVariants(prev => {
          const newSet = new Set(prev);
          newSet.delete(cartItemId);
          return newSet;
        });
        setTargetedUpdateCartItemId(null);
        return;
      }

      const cartItem = cart.find(item => item._id === cartItemId);
      if (!cartItem || !cartItem.product?.variants || cartItem.product.variants.length === 0) {
        setPendingSaveVariants(prev => {
          const newSet = new Set(prev);
          newSet.delete(cartItemId);
          return newSet;
        });
        return;
      }

      const product = cartItem.product;

      // Update all variants - keep existing variants, only update the one being changed
      const updatedVariants = cartItem.product.variants.map(v => {
        if (v.skuId === variant.skuId) {
          // Update the quantity for the variant being changed
          // console.log('Updating variant - skuId:', v.skuId, 'Old quantity:', v.quantity, 'New quantity:', newQuantity);
          return {
            ...v,
            quantity: newQuantity,
          };
        }
        // Keep other variants as-is
        return v;
      });

      // Calculate total quantity for the cart item
      const totalQuantity = updatedVariants.reduce((sum, v) => sum + (v.quantity || 0), 0);
      // console.log('Calculated totalQuantity:', totalQuantity, 'from variants:', updatedVariants.map(v => ({ skuId: v.skuId, quantity: v.quantity })));
      // Special case: If MOQ is 1 and in wholesale mode with dropshipping available, convert to 2
      const baseMOQ = product?.moq || product?.minOrderQuantity || 0;
      const isProductDropshippingAvailable = isDropshippingAvailableFromTags(product?.tagInfoList || []);
      const minMOQ = (baseMOQ === 1 && pricingMode === SellingModes.Wholesale && isProductDropshippingAvailable) ? 2 : baseMOQ;

      // For wholesale, enforce minimum quantity
      if (pricingMode === SellingModes.Wholesale && minMOQ > 0) {
        if (totalQuantity < minMOQ) {
          // Prevent quantity from going below MOQ - this should be handled in CartProductCard
          // But as a safety check, we'll also prevent it here
          // Note: The UI should prevent this, but this is a backend safety check
          cartAllProductsMOQRequiredRef.current.add(product.sku);

          // Revert the change by not updating the cart and refresh
          setPendingSaveVariants(prev => {
            const newSet = new Set(prev);
            newSet.delete(cartItemId);
            return newSet;
          });
          setTargetedUpdateCartItemId(null);
          setUpdatingVariant(null);
          await dispatch(fetchCartItems()); // Refresh to get correct quantities
          return;
        }
      }

      // pricingMode === SellingModes.Wholesale && (totalQuantity < product.quantity || totalQuantity < product.moq)
      pricingMode === SellingModes.Wholesale && minMOQ > 0 && totalQuantity < minMOQ
        ? cartAllProductsMOQRequiredRef.current.add(product.sku)
        : cartAllProductsMOQRequiredRef.current.delete(product.sku);

      // Call getFinalPrice for wholesale mode to get updated breakdown
      let calculatedBreakdown = breakdown;
      if (pricingMode === SellingModes.Wholesale) {
        try {
          const finalPriceVariants = updatedVariants
            .filter(v => (v.quantity || 0) > 0 && v.skuId)
            .map(v => ({
              skuId: v.skuId,
              selectedQuantity: v.quantity,
              price: v.calculatedFBP || v.wholesaleFinalPrice || v.finalPrice || v.price,
              ap: v.ap || v.price,
              weight: v.weight || 0
            }));

          const pricePayload = {
            offerId: product.offerId || product.offer_id || product.sku,
            variants: finalPriceVariants
          };

          const response = await ProductsApiService.getFinalPrice(pricePayload);
          if (response.status?.code === 200 && response.data) {
            const pricingData = response.data;
            // console.log('pricingData', pricingData)

            // Specialized breakdown logic as requested
            let grossTotalAmount = pricingData.finalPrice;

            // Calculate discountAmount as the difference between gross total and final price (FBP)
            const totalFinalPrice = pricingData.FBP || pricingData.finalPrice || 0;
            const discountAmount = grossTotalAmount - totalFinalPrice;

            calculatedBreakdown = {
              ...pricingData,
              discountAmount,
              grossTotalAmount
            };
          }
        } catch (error) {
          console.error('Error fetching final price in cart:', error);
        }
      }

      const cartPayload = {
        offerId: product.offerId || product.offer_id || product.sku,
        variants: updatedVariants
          .filter(v => (v.quantity || 0) > 0)
          .map(v => ({ skuId: v.skuId, quantity: v.quantity })),
        quantityFromCart: true,
        pricingMode,
        isMinMaxRange: cartItem.isMinMaxRange || false,
      };

      // Include breakdown if available (from wholesale pricing calculation)
      // EXISTING CODE COMMENTED AS REQUESTED
      /*
      if (breakdown) {
        cartPayload.breakdown = breakdown;
      }
      */

      if (calculatedBreakdown) {
        cartPayload.breakdown = calculatedBreakdown;
      }

      // console.log('API Payload:', JSON.stringify(cartPayload, null, 2));
      await CartApiService.AddUpdateCart(JSON.stringify(cartPayload));

      // Refresh cart from database
      await dispatch(fetchCartItems());

      // Clear updating state immediately after API call
      if (!signal.aborted) {
        setUpdatingVariant(null);
        setPendingSaveVariants(prev => {
          const newSet = new Set(prev);
          newSet.delete(cartItemId);
          return newSet;
        });
        // Clear the tracked quantity after successful update
        lastProcessedQuantitiesRef.current.delete(cartItemId);
        setTargetedUpdateCartItemId(null);
      }
    } catch (error) {
      if (error.name !== 'AbortError') {
      }
      // Clear states on error
      if (!signal.aborted) {
        setUpdatingVariant(null);
        setPendingSaveVariants(prev => {
          const newSet = new Set(prev);
          newSet.delete(cartItemId);
          return newSet;
        });
        setTargetedUpdateCartItemId(null);
      }
    }
  };

  // Get selected cart items - include if any variant is selected
  const selectedCart = useMemo(() => {
    return cart.filter(item => {
      if (!item.product?.variants || item.product.variants.length === 0) return false;
      return item.product.variants.some(v => selectedItems.has(`${item._id}-${v.skuId}`));
    });
  }, [cart, selectedItems]);

  const buildLeadItemsFromCheckoutCart = useCallback((items = []) => {
    const leadItems = [];
    (Array.isArray(items) ? items : []).forEach((cartItem) => {
      const variants = Array.isArray(cartItem?.variants) ? cartItem.variants : [];
      const productId = cartItem?.product?._id || cartItem?.productId || '';
      const pricingMode = cartItem?.pricingMode || 'dropshipping';

      if (variants.length > 0) {
        variants.forEach((variant) => {
          if (!selectedItems.has(`${cartItem._id}-${variant.skuId}`)) return;
          leadItems.push({
            productId,
            sku: variant?.skuId || variant?.sku || cartItem?.product?.sku || '',
            specId: variant?.specId || '',
            quantity: Number(variant?.quantity) || 1,
            pricingMode,
          });
        });
        return;
      }

      leadItems.push({
        productId,
        sku: cartItem?.product?.sku || '',
        specId: '',
        quantity: Number(cartItem?.quantity) || 1,
        pricingMode,
      });
    });
    return leadItems.filter((item) => item.productId || item.sku || item.specId);
  }, [selectedItems]);

  const selectedCartItemIds = useMemo(() => new Set(selectedCart.map((item) => item._id)), [selectedCart]);

  const filteredPriceEntriesForLead = useMemo(() => {
    if (!priceSummary?.entries) return {};
    const out = {};
    for (const [k, v] of Object.entries(priceSummary.entries)) {
      if (selectedCartItemIds.has(k)) out[k] = v;
    }
    return out;
  }, [priceSummary?.entries, selectedCartItemIds]);

  const cartStageProductsTotal = useMemo(() => {
    if (selectedCart.length === 0) return 0;
    if (Object.keys(filteredPriceEntriesForLead).length !== selectedCart.length) return 0;
    return Object.values(filteredPriceEntriesForLead).reduce((sum, e) => {
      if (e && typeof e === 'object') {
        const n = parseFloat(e.TOTAL_PRICE);
        return sum + (Number.isFinite(n) ? n : 0);
      }
      return sum;
    }, 0);
  }, [filteredPriceEntriesForLead, selectedCart.length]);

  const effectiveLMDForCartLead =
    shippingMethod === 'self-pickup' ? 0 : totalLMDWithGST || totalLMDCharges || 0;

  useEffect(() => {
    const auth = getAuthLocalStorage();
    if (!auth.isLoggedIn || !auth.loginHeader) return;
    if (!selectedCart.length || cartStageProductsTotal <= 0) return;
    const cartValue = cartStageProductsTotal;
    if (cartValue <= 0) return;

    const leadItems = buildLeadItemsFromCheckoutCart(selectedCart);
    if (!leadItems.length) return;

    const t = setTimeout(() => {
      CartApiService.recordAbandonedLead({
        stage: 'cart',
        cartValue,
        totalLMDCharges: effectiveLMDForCartLead,
        gstOnLMD: gstOnLMD || 0,
        leadItems,
      }).catch(() => { });
    }, 1200);

    return () => clearTimeout(t);
  }, [
    selectedCart,
    cartStageProductsTotal,
    effectiveLMDForCartLead,
    gstOnLMD,
    buildLeadItemsFromCheckoutCart,
  ]);

  // Handle Select All / Deselect All - check all variants across all items
  const allSelectedVariants = useMemo(() => {
    if (cart.length === 0) return false;
    return cart.every(item => {
      if (!item.product?.variants || item.product.variants.length === 0) return false;
      return item.product.variants.every(v => selectedItems.has(`${item._id}-${v.skuId}`));
    });
  }, [cart, selectedItems]);

  const someSelectedVariants = useMemo(() => {
    const hasAnySelected = cart.some(item => {
      if (!item.product?.variants || item.product.variants.length === 0) return false;
      return item.product.variants.some(v => selectedItems.has(`${item._id}-${v.skuId}`));
    });
    return hasAnySelected && !allSelectedVariants;
  }, [cart, selectedItems, allSelectedVariants]);

  const handleSelectAll = () => {
    if (allSelectedVariants) {
      // Deselect all variants
      setSelectedItems(new Set());
    } else {
      // Select all variants across all cart items
      const allVariants = new Set();
      cart.forEach(item => {
        if (item.product?.variants && item.product.variants.length > 0) {
          item.product.variants.forEach(v => {
            allVariants.add(`${item._id}-${v.skuId}`);
          });
        }
      });
      setSelectedItems(allVariants);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="container mx-auto px-0 py-0">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-4">
            {/* Select All Header */}
            {/* cart.length > 0 && (
              <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-4">
                <div className="flex items-center gap-3">
                  <button onClick={handleSelectAll} className="flex items-center gap-2 text-blue-600 hover:text-blue-700 transition-colors">
                    {allSelectedVariants ? (
                      <IoCheckbox size={16} className="text-blue-600" />
                    ) : someSelectedVariants ? (
                      <IoCheckboxOutline size={16} className="text-blue-600" />
                    ) : (
                      <IoSquareOutline size={16} />
                    )}
                    <span className="text-[14px] font-medium">Select All</span>
                  </button>
                  <span className="text-[13px] text-gray-600">({selectedItems.size} variants selected)</span>
                </div>
              </div>
            )*/}

            {/* Product Cards */}
            {cartStatus === 'loading' ? (
              <div className="bg-white rounded-lg shadow-sm p-8 text-center relative min-h-[200px]">
                <Loader value={true} variant="overlay" size="large" text="" />
              </div>
            ) : cart.length > 0 ? (
              cart.map(cartItem => {
                // Generate key with variant quantities to force re-render on updates
                const quantityKey = (cartItem.product?.variants || []).map(v => `${v.skuId}:${v.quantity}`).join('|');
                const cardKey = `${cartItem._id}-${quantityKey}`;

                return (
                  <CartProductCard
                    key={cardKey}
                    product={cartItem.product}
                    isMinMaxRange={cartItem.isMinMaxRange || false}
                    pricingMode={cartItem.pricingMode}
                    priceRanges={cartItem.priceRanges}
                    variants={cartItem.product?.variants || []}
                    cartItemId={cartItem._id}
                    selectedItems={selectedItems}
                    onSelect={handleSelectItem}
                    onQuantityChange={handleQuantityChange}
                    updatingVariant={updatingVariant}
                    pendingSaveVariants={pendingSaveVariants}
                    setPriceSummary={setPriceSummary}
                    setAllLoadingCard={setAllLoadingCard}
                    cartAllProductsMOQRequiredRef={cartAllProductsMOQRequiredRef}
                    targetedUpdateCartItemId={targetedUpdateCartItemId}
                    breakdown={cartItem.pricingBreakdown}
                  />
                );
              })
            ) : (
              <div className="bg-white rounded-lg shadow-sm p-8 text-center">
                <p className="text-gray-500">Your cart is empty</p>
              </div>
            )}
          </div>
          <div className="lg:col-span-1">
            <CheckOutBox
              type="cart"
              cart={selectedCart}
              allCartItems={cart}
              selectedItems={selectedItems}
              isAllLoadingCard={isAllLoadingCard}
              hasAllProductCardsMinMOQ={cartAllProductsMOQRequiredRef.current.size === 0}
              pendingSaveVariants={pendingSaveVariants}
              cartTotalFinalPrice={cartTotalFinalPrice}
              cartLMD={cartLMD}
              cartTotalLMD={cartTotalLMDValue}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

export { CheckOutBox, CartProductQuantity };
